import { infiniteApiQuery } from "utils/queries/infiniteQueries";
import { transformLearningPlan } from "services/learningPlans/utils";
import { LessonFeedback, LessonSession, Plan } from "services/sessions/model";
import { UserTag } from "services/users/model";
import { apiQuery, PaginatedResponse } from "utils/queries";
import { api } from "../restUtil";
import { Student } from "./model";

type ApiStatusResponse = {
  code: string;
  message: string;
  title: string;
};

type StudentCurrentPlanResponseItem = Plan & {
  start_date: string;
  sessions?: LessonSession[];
};

export type StudentPlan = StudentCurrentPlanResponseItem & {
  from: string;
  to: string;
  number_of_sessions: number;
  frequencyString: string;
};

export type StudentWithPlan = Student & {
  first_name?: string;
  last_name?: string | null;
  plan?: StudentPlan;
  sessions?: LessonSession[];
};

type CurrentStudentPlanResponse = {
  plan: StudentCurrentPlanResponseItem | null;
};

type FetchStudentWithPlanParams = {
  id: string;
};

type StudentParticipant = {
  id: Student["id"];
  status: Student["status"];
  profile: {
    email: Student["profile"]["email"];
    first_name: Student["profile"]["first_name"];
    last_name: Student["profile"]["last_name"];
    timezone: Student["profile"]["timezone"];
    address: {
      city: Student["profile"]["city"];
      country: Student["profile"]["country"];
    };
  };
  student: {
    number_of_lessons: Student["number_of_lessons"];
    status: Student["status"];
    credits: Student["credits"];
  };
  primary_instructor: Student["primary_instructor"];
};

function normalizeStudentParticipant(student: Student): StudentParticipant {
  return {
    id: student.id,
    status: student.status,
    profile: {
      email: student.profile.email,
      first_name: student.profile.first_name,
      last_name: student.profile.last_name,
      timezone: student.profile.timezone,
      address: {
        city: student.profile.city,
        country: student.profile.country,
      },
    },
    student: {
      number_of_lessons: student.number_of_lessons,
      status: student.status,
      credits: student.credits,
    },
    primary_instructor: student.primary_instructor,
  };
}

export const fetchStudentProfileQuery = apiQuery<Student>({
  key: "/student",
  queryFn: fetchStudentProfile,
});

export function fetchStudentProfile() {
  return api.get<Student>("/student");
}

export async function fetchStudent(id: string) {
  const student = await api.get<Student>(`/student/${id}`);
  return normalizeStudentParticipant(student);
}

/**
 * The student record plus their current plan. Two endpoints, one cache entry, so a
 * plan edit can invalidate the pair instead of the page hand-merging the response.
 */
export const studentWithPlanQuery = apiQuery<StudentWithPlan, FetchStudentWithPlanParams>(
  {
    key: "/student/:id/with-plan",
    queryFn: ({ params }) => fetchStudentWithPlan(params),
  }
);

export async function fetchStudentWithPlan(args: FetchStudentWithPlanParams) {
  // Neither request depends on the other, so they run together rather than
  // costing two sequential round-trips before the page can paint.
  const [student, planResp] = await Promise.all([
    api.get<StudentWithPlan>(`/student/${args.id}`),
    api.get<CurrentStudentPlanResponse>(`/student/${args.id}/plan/current`),
  ]);

  if (planResp.plan) {
    const transformedPlan: StudentPlan = transformLearningPlan(planResp.plan);
    student.first_name = student.profile.first_name;
    student.last_name = student.profile.last_name;
    student.plan = transformedPlan;
    student.sessions ??= transformedPlan.sessions;
  }

  return student;
}

type FetchStudentActivityParams = {
  from: string;
  to: string;
};

type FetchStudentActivityResponse = {
  active_dates: FetchStudentActivityParams & {
    list: string[];
  };
  current_streak: number;
};

export const fetchStudentActivity = apiQuery<
  FetchStudentActivityResponse,
  FetchStudentActivityParams
>({
  key: "/student/activity",
});

type DeleteStudentPlanParams = {
  student_id: string;
  plan_id: string;
};

export function deleteStudentPlan(args: DeleteStudentPlanParams) {
  return api.delete(
    `/student/${args.student_id}/plan?plan_id=${encodeURIComponent(args.plan_id)}`
  );
}

type UpdateStudentLevelPayload = {
  score: Student["score"];
};

export function updateStudentLevel(
  studentId: string,
  payload: UpdateStudentLevelPayload
) {
  return api.patch<UpdateStudentLevelPayload, ApiStatusResponse>(
    `/student/${studentId}/level`,
    payload
  );
}

type FetchStudentCompletedFeedbackParams = {
  student_id: string;
  empty?: boolean;
  page?: number;
  size?: number;
};

type StudentCompletedFeedbackSession = {
  id: LessonSession["id"];
  name: LessonSession["name"];
  description: LessonSession["description"];
  scheduled: LessonSession["scheduled"];
  instructor: LessonSession["instructor"];
  plan: LessonSession["plan"];
  feedback: LessonFeedback;
};

type FetchStudentCompletedFeedbackResponse =
  PaginatedResponse<StudentCompletedFeedbackSession>;

export const studentCompletedFeedbackQuery = infiniteApiQuery<
  Awaited<ReturnType<typeof fetchStudentCompletedFeedback>>["list"][number],
  { student_id: string; empty: boolean }
>({
  path: "/student/:id/sessions/lesson/completed/feedback",
  queryFn: (_path, params) => fetchStudentCompletedFeedback(params),
});

export async function fetchStudentCompletedFeedback(
  args: FetchStudentCompletedFeedbackParams
) {
  const resp = await api.get<FetchStudentCompletedFeedbackResponse>(
    `/student/${args.student_id}/sessions/lesson/completed/feedback`,
    {
      empty: args.empty,
      page: args.page,
      size: args.size,
    }
  );

  return resp;
}

export async function isFirstSession() {
  const resp = await fetchStudentProfile();
  return {
    assessment_session: resp.number_of_lessons === 0,
  };
}

export type PatchStudentProfilePayload = {
  learning_reasons?: string | null;
  things_of_interest?: string | null;
  industry?: string | null;
  type?: "self-study";
  weekly_activity_goal?: number;
  daily_activity_goal?: number;
};

export function patchStudentProfile(args: PatchStudentProfilePayload) {
  return api.patch<PatchStudentProfilePayload, Student>("/student", args);
}

export function postSetupCompleted() {
  return api.post<undefined, ApiStatusResponse>("/student/setup/completed");
}

// Whether a trial or a package brought them here, there is nothing to book until a
// coach is chosen: an admin assigns the coach with the first booking, from the
// matching calendar.
export function isWaitingForCoach(student: Student | undefined) {
  return (
    student?.type === "one-on-one" &&
    student.status === "paying" &&
    student.primary_instructor === null
  );
}

type StudentTagsPayload = {
  tags: string[];
};

type StudentTagsResponse = {
  tags: UserTag[];
};

export const myStudentTagsQuery = apiQuery<StudentTagsResponse>({
  key: "/student/tags",
  queryFn: getTags,
});

export function getTags() {
  return api.get<StudentTagsResponse>("/student/tags");
}

export function addTags(args: StudentTagsPayload) {
  return api.post<StudentTagsPayload, ApiStatusResponse>("/student/tags", {
    tags: args.tags,
  });
}

export function removeTags(args: StudentTagsPayload) {
  return api.delete<StudentTagsPayload>("/student/tags", {
    tags: args.tags,
  });
}
